import { get, IHttpRequestConfig, put } from "@/browser/http";
import { applyHeaders, parseResponse } from "@/core/api";
import { DateIsoString } from "@/core/types";
import { compose, IURLPageConfig, IURLSearchParams, IURLSortConfig } from "@/core/url";
import { Attachment } from "@/data/attachments";

import { LifecyclePageable } from "../lifecycle";
import { TimeReport, TimeReportPageable, TimeReportProjectItem, TimeReportVersionPageable } from "./types";
import { UpdateTimeReportProjectItemResult } from "./types/UpdateTimeReportProjectItemResult";

const API = {
  list: "/api/time-reports",
  details: "/api/time-reports/{timeReportGuid}",
  update: "/api/time-reports/{timeReportGuid}",
  updateProjectItem: "/api/time-reports/{timeReportGuid}/project/{projectGuid}/item",
  revisions: "/api/time-reports/{timeReportGuid}/revisions",
  lifecycle: "/api/time-reports/{timeReportGuid}/lifecycle",
  submit: "/api/time-reports/{timeReportGuid}/submit",
};

export interface FetchTimeReportsParams extends Partial<IURLPageConfig>, Partial<IURLSortConfig> {
  companyId?: number;
}

export const fetchTimeReports = (params: FetchTimeReportsParams, options?: IHttpRequestConfig) => {
  const { companyId, ...restParams } = params;

  const url = compose(API.list, {}, restParams);

  return get(url, applyHeaders({ companyId }, options)).then(parseResponse<TimeReportPageable>(TimeReportPageable));
};

export interface FetchTimeReportParams {
  timeReportGuid: string;
  companyId?: number;
}

export const fetchTimeReport = (params: FetchTimeReportParams, options?: IHttpRequestConfig) => {
  const { companyId, timeReportGuid, ...restParams } = params;

  const url = compose(API.details, { timeReportGuid }, restParams);

  return get(url, applyHeaders({ companyId }, options)).then(parseResponse<TimeReport>(TimeReport));
};

export interface FetchTimeReportVersionsParams extends Partial<IURLPageConfig>, Partial<IURLSortConfig> {
  timeReportGuid: string;
  companyId?: number;
}

export const fetchTimeReportVersions = (params: FetchTimeReportVersionsParams, options?: IHttpRequestConfig) => {
  const { companyId, timeReportGuid, ...restParams } = params;

  const url = compose(API.revisions, { timeReportGuid }, restParams);

  return get(url, applyHeaders({ companyId }, options)).then(
    parseResponse<TimeReportVersionPageable>(TimeReportVersionPageable),
  );
};

export interface FetchTimeReportLifecycleParams extends IURLSearchParams {
  timeReportGuid: string;
  companyId?: number;
}

export const fetchTimeReportLifecycle = (params: FetchTimeReportLifecycleParams, options?: IHttpRequestConfig) => {
  const { companyId, timeReportGuid, ...restParams } = params;

  const url = compose(
    API.lifecycle,
    { timeReportGuid },
    {
      ...restParams,
      sortConfig: [
        {
          sortBy: "createdDate",
          order: "DESC",
        },
      ],
    },
  );

  return get(url, applyHeaders({ companyId }, options)).then(parseResponse<LifecyclePageable>(LifecyclePageable));
};

export interface UpdateTimeReportParams {
  timeReportGuid: string;
  companyId?: number;
}

export const updateTimeReport = (params: UpdateTimeReportParams, data: TimeReport, options?: IHttpRequestConfig) => {
  const { timeReportGuid, companyId } = params;

  const url = compose(API.update, { timeReportGuid }, {});

  return put(url, data, applyHeaders({ companyId }, options)).then(parseResponse<TimeReport>(TimeReport));
};

export interface UpdateTimeReportProjectItemParams {
  timeReportGuid: string;
  projectGuid: string;
  timeReportLastReadDate: DateIsoString;
  companyId?: number;
}

export const updateTimeReportProjectItem = (
  params: UpdateTimeReportProjectItemParams,
  data: TimeReportProjectItem,
  options?: IHttpRequestConfig,
) => {
  const { timeReportGuid, projectGuid, timeReportLastReadDate, companyId } = params;

  const url = compose(API.updateProjectItem, { timeReportGuid, projectGuid }, { timeReportLastReadDate });

  return put(url, data, applyHeaders({ companyId }, options)).then(
    parseResponse<UpdateTimeReportProjectItemResult>(UpdateTimeReportProjectItemResult),
  );
};

export interface SubmitTimeReportDto {
  externalComment?: string;
  attachments?: Attachment[];
  lastReadDate: string;
}

export interface SubmitTimeReportParams {
  timeReportGuid: string;
  companyId?: number;
}

export const submitTimeReport = (
  params: SubmitTimeReportParams,
  data: SubmitTimeReportDto,
  options?: IHttpRequestConfig,
) => {
  const { companyId, timeReportGuid, ...restParams } = params;

  const url = compose(API.submit, { timeReportGuid }, restParams);

  return put(url, data, applyHeaders({ companyId }, options)).then(parseResponse<TimeReport>(TimeReport));
};
